/**
 * SoundBYTE Originals Store
 *
 * Manages the bespoke commissioned artwork journey state.
 * Supports three tiers: Low, Medium, High customization.
 *
 * Low Tier Flow:
 * 0. Choose Tier
 * 1. Voice + Images
 * 2. Review & Pay ($2,500)
 * 3. Awaiting Creation
 *
 * Medium/High Tier Flow:
 * 0. Choose Tier
 * 1. Your Story (questionnaire - free)
 * 2. Your Preferences (colors, styles, timeline)
 * 3. Review & Submit
 * 4. Awaiting Review (admin review)
 * 5. Creation Retainer ($3,000)
 * 6. Artwork Creation & Refinement
 * 7. Approval, Production & Delivery
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { requestService } from '@soundbyte/services';
import type { OriginalsTierId, OriginalsTierData } from '@soundbyte/types';
import {
  ORIGINALS_TIERS,
  getOriginalsTier,
  ORIGINALS_VALIDATION,
  calculateLowTierPrice,
  SONIC_PHYSICAL_EDITIONS,
} from '@soundbyte/config';
import type { SonicSizeId, SonicMaterialId, SonicPhysicalEditionId } from '@soundbyte/config';

// Step definitions (0-8, where 0 is tier selection)
export type OriginalsStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

// Project status (internal tracking)
export type ProjectStatus =
  | 'REQUEST_SUBMITTED'
  | 'UNDER_REVIEW'
  | 'WAITLISTED'
  | 'INVITED_TO_PROCEED'
  | 'QUOTE_ISSUED'
  | 'RETAINER_PAID'
  | 'DIGITAL_CREATED'
  | 'AWAITING_REFINEMENT'
  | 'AWAITING_FINAL_APPROVAL'
  | 'ARTWORK_LOCKED'
  | 'PAID_IN_FULL'
  | 'IN_PRODUCTION'
  | 'SHIPPED'
  | 'COMPLETED';

// Story pillar data for inspiration
export interface StoryPillar {
  notes: string;
  tags: string[];
}

// Reference file data
export interface ReferenceFile {
  id: string;
  file: File | null;
  url: string;
  name: string;
  type: 'image' | 'document' | 'audio';
  caption: string;
}

// Low tier reference image
export interface LowTierImage {
  id: string;
  file: File | null;
  url: string;
  name: string;
}

// Step 1 - Request Submission
export interface RequestData {
  // Questionnaire
  projectName: string;
  projectDescription: string;
  inspiration: string;

  // Story pillars (enhanced inspiration) - Medium/High tier
  storyPillars: {
    meaning: StoryPillar;      // Name significance, story behind it
    places: StoryPillar;       // Birthplace, cities, memorable locations
    passions: StoryPillar;     // Hobbies, interests, lifestyle
    people: StoryPillar;       // Family, relationships, loved ones
    personality: StoryPillar;  // Traits, quirks, beliefs
    favorites: StoryPillar;    // Quotes, music, meaningful objects
  };

  // Reference files (photos, docs, logos) - Medium/High tier
  referenceFiles: ReferenceFile[];

  // Low tier specific - reference images (up to 5)
  lowTierImages: LowTierImage[];
  lowTierImageUrls: string[];
  // Low tier specific - personal image (1 required)
  personalImage: File | null;
  personalImageUrl: string | null;
  personalImageId: string | null;
  // Low tier specific - selected upgrades
  selectedUpgrades: string[];
  // Low tier specific - brief description (optional)
  briefDescription: string;

  // Preferences (moved to step 2) - Medium/High tier
  colorPreferences: string[];
  stylePreferences: string[];
  intendedUse: string; // personal, gift, corporate, etc.
  additionalNotes: string;

  // Voice upload - ALL tiers (voice is core to SoundBYTE art)
  voiceFile: File | null;
  voiceUrl: string | null;
  voiceDuration: number;
  waveformGuideBase64: string | null;

  // Preferences - Medium/High tier
  preferredSize: 'small' | 'medium' | 'large' | 'undecided';
  preferredMaterial: 'paper' | 'canvas' | 'metal' | 'undecided';
  wantsFraming: boolean | null;

  // Timeline - Medium/High tier
  desiredTimeline: 'flexible' | '1-2months' | '3-6months' | 'specific';
  specificDate: string | null;
  isGift: boolean;
  giftOccasion: string | null;
}

// Step 2 - Quote data (from admin)
export interface QuoteData {
  quoteId: string | null;
  estimatedPriceMin: number;
  estimatedPriceMax: number;
  estimatedDelivery: string | null;
  adminNotes: string | null;
  quotedAt: string | null;
}

// Step 3 - Retainer payment
export interface RetainerData {
  retainerAmount: number;
  retainerPaid: boolean;
  paymentDate: string | null;
  transactionId: string | null;
}

// Step 4 - Creation & Refinement
export interface CreationData {
  artworkUrl: string | null;
  artworkCreatedAt: string | null;
  refinements: {
    feedback: string;
    submittedAt: string;
    respondedAt: string | null;
  }[];
  maxRefinements: number;
}

// Step 5 - Approval
export interface ApprovalData {
  approved: boolean;
  approvedAt: string | null;
  digitalMasterFile: string | null;
}

// Step 6 - Physical Production
export interface PhysicalData {
  selectedEdition: SonicPhysicalEditionId | null; // New: predefined edition
  // Legacy fields (kept for backward compatibility)
  selectedSize: SonicSizeId | null;
  selectedMaterial: SonicMaterialId | null;
  selectedFrame: boolean;
  finalPrice: number;
  retainerCredit: number;
  balanceDue: number;
  balancePaid: boolean;
  shippingAddress: {
    fullName: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
  };
  trackingNumber: string | null;
  estimatedDeliveryDate: string | null;
}

// Order data
export interface OriginalsOrderData {
  projectId: string | null;
  status: ProjectStatus;
  createdAt: string | null;
  completedAt: string | null;
}

interface OriginalsState {
  // Tier selection
  selectedTier: OriginalsTierId | null;

  // Current step (user-facing, 0 = tier selection)
  currentStep: OriginalsStep;

  // Step data
  request: RequestData;
  quote: QuoteData;
  retainer: RetainerData;
  creation: CreationData;
  approval: ApprovalData;
  physical: PhysicalData;
  order: OriginalsOrderData;

  // UI state
  isSubmitting: boolean;
  error: string | null;

  // Tier actions
  setTier: (tier: OriginalsTierId) => void;
  getTierConfig: () => typeof ORIGINALS_TIERS[number] | undefined;
  getRetainerAmount: () => number;
  isLowTier: () => boolean;
  resetForTierChange: () => void;

  // Navigation
  setStep: (step: OriginalsStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  canProceed: () => boolean;
  getValidationErrors: () => string[];

  // Step 1 - Request
  setRequestField: <K extends keyof Omit<RequestData, 'voiceFile' | 'voiceUrl' | 'voiceDuration' | 'storyPillars' | 'referenceFiles'>>(
    field: K,
    value: RequestData[K]
  ) => void;
  setVoiceFile: (file: File | null, url?: string | null, duration?: number, waveformGuide?: string | null) => void;
  addColorPreference: (color: string) => void;
  removeColorPreference: (color: string) => void;
  addStylePreference: (style: string) => void;
  removeStylePreference: (style: string) => void;

  // Story pillars
  setStoryPillarNotes: (pillar: keyof RequestData['storyPillars'], notes: string) => void;
  addStoryPillarTag: (pillar: keyof RequestData['storyPillars'], tag: string) => void;
  removeStoryPillarTag: (pillar: keyof RequestData['storyPillars'], tag: string) => void;

  // Reference files (Medium/High tier)
  addReferenceFile: (file: File, type: ReferenceFile['type']) => void;
  removeReferenceFile: (id: string) => void;
  updateReferenceFileCaption: (id: string, caption: string) => void;

  // Low tier images
  addLowTierImage: (file: File) => void;
  removeLowTierImage: (id: string) => void;
  setPersonalImage: (file: File | null, url?: string | null) => void;
  toggleUpgrade: (upgradeId: string) => void;
  setBriefDescription: (description: string) => void;

  // Step 2 - Quote (set by admin/system)
  setQuoteData: (data: Partial<QuoteData>) => void;

  // Step 3 - Retainer
  setRetainerPaid: (paid: boolean, transactionId?: string) => void;

  // Step 4 - Creation
  setArtworkUrl: (url: string) => void;
  addRefinement: (feedback: string) => void;

  // Step 5 - Approval
  approveArtwork: () => void;

  // Step 6 - Physical
  setPhysicalField: <K extends keyof Omit<PhysicalData, 'shippingAddress'>>(
    field: K,
    value: PhysicalData[K]
  ) => void;
  setShippingField: <K extends keyof PhysicalData['shippingAddress']>(
    field: K,
    value: PhysicalData['shippingAddress'][K]
  ) => void;
  getPhysicalPrice: () => { total: number; breakdown: { label: string; amount: number }[] };
  getPhysicalValidationErrors: () => Record<string, string>;
  canProceedPhysical: () => boolean;

  // Order management
  setProjectId: (id: string) => void;
  setStatus: (status: ProjectStatus) => void;

  // Backend submission
  submitRequest: () => Promise<boolean>;
  uploadVoice: () => Promise<string | null>;
  uploadReferences: () => Promise<{ id: string; url: string; name: string; type: string }[]>;
  uploadLowTierImages: () => Promise<{ id: string; url: string; name: string }[]>;

  // Reset
  resetWizard: () => void;
  setSubmitting: (submitting: boolean) => void;
  setError: (error: string | null) => void;
}

const initialStoryPillar: StoryPillar = {
  notes: '',
  tags: [],
};

const initialRequest: RequestData = {
  projectName: '',
  projectDescription: '',
  inspiration: '',
  storyPillars: {
    meaning: { ...initialStoryPillar },
    places: { ...initialStoryPillar },
    passions: { ...initialStoryPillar },
    people: { ...initialStoryPillar },
    personality: { ...initialStoryPillar },
    favorites: { ...initialStoryPillar },
  },
  referenceFiles: [],
  // Low tier specific
  lowTierImages: [],
  lowTierImageUrls: [],
  personalImage: null,
  personalImageUrl: null,
  personalImageId: null,
  selectedUpgrades: [],
  briefDescription: '',
  // Preferences
  colorPreferences: [],
  stylePreferences: [],
  intendedUse: '',
  additionalNotes: '',
  voiceFile: null,
  voiceUrl: null,
  voiceDuration: 0,
  waveformGuideBase64: null,
  preferredSize: 'undecided',
  preferredMaterial: 'undecided',
  wantsFraming: null,
  desiredTimeline: 'flexible',
  specificDate: null,
  isGift: false,
  giftOccasion: null,
};

const initialQuote: QuoteData = {
  quoteId: null,
  estimatedPriceMin: 0,
  estimatedPriceMax: 0,
  estimatedDelivery: null,
  adminNotes: null,
  quotedAt: null,
};

const initialRetainer: RetainerData = {
  retainerAmount: 3500,
  retainerPaid: false,
  paymentDate: null,
  transactionId: null,
};

const initialCreation: CreationData = {
  artworkUrl: null,
  artworkCreatedAt: null,
  refinements: [],
  maxRefinements: 2,
};

const initialApproval: ApprovalData = {
  approved: false,
  approvedAt: null,
  digitalMasterFile: null,
};

const initialPhysical: PhysicalData = {
  selectedEdition: null, // No edition selection - Medium Canvas (Framed) is included
  // Legacy fields - reflect the included material (Medium Canvas Framed)
  selectedSize: 'medium',
  selectedMaterial: 'canvas',
  selectedFrame: true, // Framed is included
  finalPrice: 0,
  retainerCredit: 3500,
  balanceDue: 0,
  balancePaid: false,
  shippingAddress: {
    fullName: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
  },
  trackingNumber: null,
  estimatedDeliveryDate: null,
};

const initialOrder: OriginalsOrderData = {
  projectId: null,
  status: 'REQUEST_SUBMITTED',
  createdAt: null,
  completedAt: null,
};

export const useOriginalsStore = create<OriginalsState>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedTier: null,
      currentStep: 0, // Start at tier selection
      request: { ...initialRequest },
      quote: { ...initialQuote },
      retainer: { ...initialRetainer },
      creation: { ...initialCreation },
      approval: { ...initialApproval },
      physical: { ...initialPhysical },
      order: { ...initialOrder },
      isSubmitting: false,
      error: null,

      // Tier actions
      setTier: (tier) => {
        const currentTier = get().selectedTier;
        if (currentTier !== tier) {
          // Reset data when changing tiers, but preserve voice recording
          const { request } = get();
          set({
            selectedTier: tier,
            request: {
              ...initialRequest,
              // Preserve voice recording as it's core to all SoundBYTE art
              voiceFile: request.voiceFile,
              voiceUrl: request.voiceUrl,
              voiceDuration: request.voiceDuration,
              waveformGuideBase64: request.waveformGuideBase64,
            },
            // Update retainer amount based on tier
            retainer: {
              ...initialRetainer,
              retainerAmount: getOriginalsTier(tier)?.retainerAmount ?? 0,
            },
            error: null,
          });
        } else {
          set({ selectedTier: tier, error: null });
        }
      },

      getTierConfig: () => {
        const { selectedTier } = get();
        return selectedTier ? getOriginalsTier(selectedTier) : undefined;
      },

      getRetainerAmount: () => {
        const { selectedTier } = get();
        return selectedTier ? (getOriginalsTier(selectedTier)?.retainerAmount ?? 0) : 0;
      },

      isLowTier: () => get().selectedTier === 'low',

      resetForTierChange: () => {
        const { request } = get();
        set({
          selectedTier: null,
          currentStep: 0,
          request: {
            ...initialRequest,
            // Preserve voice recording
            voiceFile: request.voiceFile,
            voiceUrl: request.voiceUrl,
            voiceDuration: request.voiceDuration,
            waveformGuideBase64: request.waveformGuideBase64,
          },
          error: null,
        });
      },

      // Navigation
      setStep: (step) => set({ currentStep: step }),

      nextStep: () => {
        const { currentStep, selectedTier } = get();
        // Max step: Low tier = 4, Medium/High tier = 8 (with physical delivery step)
        const maxStep = selectedTier === 'low' ? 4 : 8;
        if (currentStep < maxStep) {
          set({ currentStep: (currentStep + 1) as OriginalsStep, error: null });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: (currentStep - 1) as OriginalsStep, error: null });
        }
      },

      canProceed: () => {
        const { currentStep, selectedTier, request, canProceedPhysical } = get();

        // Step 0: Tier Selection
        if (currentStep === 0) {
          return selectedTier !== null;
        }

        // Low tier validation
        if (selectedTier === 'low') {
          switch (currentStep) {
            case 1:
              // Voice + Images step - commission name, voice, and personal image required
              const hasCommissionName = request.projectName.trim().length >= 1;
              const hasVoice = request.voiceUrl !== null || request.voiceFile !== null;
              const hasPersonalImage = request.personalImageUrl !== null || request.personalImage !== null;
              return hasCommissionName && hasVoice && hasPersonalImage;
            case 2:
              // Physical Delivery step - validate shipping address
              return canProceedPhysical();
            case 3:
              // Review step - all previous validations must pass
              const validName = request.projectName.trim().length >= 1;
              const validVoice = request.voiceUrl !== null || request.voiceFile !== null;
              const validImage = request.personalImageUrl !== null || request.personalImage !== null;
              return validName && validVoice && validImage && canProceedPhysical();
            default:
              return true;
          }
        }

        // Medium/High tier validation
        switch (currentStep) {
          case 1:
            // Step 1: Your Story - requires project name, description (10+ chars), voice recording
            const hasProjectName = request.projectName.trim().length >= 1;
            const hasDescription = request.projectDescription.trim().length >= 10;
            const hasVoiceRecording = request.voiceUrl !== null || request.voiceFile !== null;
            return hasProjectName && hasDescription && hasVoiceRecording;

          case 2:
            // Step 2: Preferences - desiredTimeline is required (has default)
            return request.desiredTimeline.length >= 1;

          case 3:
            // Step 3: Physical Delivery - validate shipping address
            return canProceedPhysical();

          case 4:
            // Step 4: Review - all required fields must be valid
            const validProjectName = request.projectName.trim().length >= 1;
            const validDescription = request.projectDescription.trim().length >= 10;
            const validTimeline = request.desiredTimeline.length >= 1;
            const validVoice = request.voiceUrl !== null || request.voiceFile !== null;
            return validProjectName && validDescription && validTimeline && validVoice && canProceedPhysical();

          default:
            return currentStep < 8;
        }
      },

      // Get validation errors for current step
      getValidationErrors: () => {
        const { currentStep, selectedTier, request, getPhysicalValidationErrors } = get();
        const errors: string[] = [];

        // Step 0: Tier Selection
        if (currentStep === 0) {
          if (!selectedTier) {
            errors.push('Please select a customization tier');
          }
          return errors;
        }

        // Low tier validation
        if (selectedTier === 'low') {
          if (currentStep === 1) {
            const hasCommissionName = request.projectName.trim().length >= 1;
            const hasVoice = request.voiceUrl !== null || request.voiceFile !== null;
            const hasPersonalImage = request.personalImageUrl !== null || request.personalImage !== null;
            if (!hasCommissionName) {
              errors.push('Commission name is required');
            }
            if (!hasVoice) {
              errors.push('Voice recording is required');
            }
            if (!hasPersonalImage) {
              errors.push('Personal image is required');
            }
          }
          if (currentStep === 2 || currentStep === 3) {
            // Physical step validation
            const physicalErrors = getPhysicalValidationErrors();
            Object.values(physicalErrors).forEach((err) => errors.push(err));
          }
          return errors;
        }

        // Medium/High tier validation
        if (currentStep === 1 || currentStep === 4) {
          if (request.projectName.trim().length < 1) {
            errors.push('Project name is required');
          }
          if (request.projectDescription.trim().length < 10) {
            errors.push('Project description must be at least 10 characters');
          }
          const hasVoice = request.voiceUrl !== null || request.voiceFile !== null;
          if (!hasVoice) {
            errors.push('Voice recording is required');
          }
        }

        if (currentStep === 2 || currentStep === 4) {
          if (request.desiredTimeline.length < 1) {
            errors.push('Timeline is required');
          }
        }

        if (currentStep === 3 || currentStep === 4) {
          // Physical step validation
          const physicalErrors = getPhysicalValidationErrors();
          Object.values(physicalErrors).forEach((err) => errors.push(err));
        }

        return errors;
      },

      // Step 1 - Request
      setRequestField: (field, value) =>
        set({ request: { ...get().request, [field]: value } }),

      setVoiceFile: (file, url = null, duration = 0, waveformGuide = null) =>
        set({
          request: {
            ...get().request,
            voiceFile: file,
            voiceUrl: url,
            voiceDuration: duration,
            waveformGuideBase64: waveformGuide,
          },
        }),

      addColorPreference: (color) => {
        const { request } = get();
        if (!request.colorPreferences.includes(color) && request.colorPreferences.length < 5) {
          set({
            request: {
              ...request,
              colorPreferences: [...request.colorPreferences, color],
            },
          });
        }
      },

      removeColorPreference: (color) => {
        const { request } = get();
        set({
          request: {
            ...request,
            colorPreferences: request.colorPreferences.filter((c) => c !== color),
          },
        });
      },

      addStylePreference: (style) => {
        const { request } = get();
        if (!request.stylePreferences.includes(style) && request.stylePreferences.length < 5) {
          set({
            request: {
              ...request,
              stylePreferences: [...request.stylePreferences, style],
            },
          });
        }
      },

      removeStylePreference: (style) => {
        const { request } = get();
        set({
          request: {
            ...request,
            stylePreferences: request.stylePreferences.filter((s) => s !== style),
          },
        });
      },

      // Story pillars
      setStoryPillarNotes: (pillar, notes) => {
        const { request } = get();
        set({
          request: {
            ...request,
            storyPillars: {
              ...request.storyPillars,
              [pillar]: {
                ...request.storyPillars[pillar],
                notes,
              },
            },
          },
        });
      },

      addStoryPillarTag: (pillar, tag) => {
        const { request } = get();
        const currentPillar = request.storyPillars[pillar];
        if (!currentPillar.tags.includes(tag) && currentPillar.tags.length < 10) {
          set({
            request: {
              ...request,
              storyPillars: {
                ...request.storyPillars,
                [pillar]: {
                  ...currentPillar,
                  tags: [...currentPillar.tags, tag],
                },
              },
            },
          });
        }
      },

      removeStoryPillarTag: (pillar, tag) => {
        const { request } = get();
        const currentPillar = request.storyPillars[pillar];
        set({
          request: {
            ...request,
            storyPillars: {
              ...request.storyPillars,
              [pillar]: {
                ...currentPillar,
                tags: currentPillar.tags.filter((t) => t !== tag),
              },
            },
          },
        });
      },

      // Reference files
      addReferenceFile: (file, type) => {
        const { request } = get();
        if (request.referenceFiles.length < 10) {
          const newFile: ReferenceFile = {
            id: `ref-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            url: URL.createObjectURL(file),
            name: file.name,
            type,
            caption: '',
          };
          set({
            request: {
              ...request,
              referenceFiles: [...request.referenceFiles, newFile],
            },
          });
        }
      },

      removeReferenceFile: (id) => {
        const { request } = get();
        const fileToRemove = request.referenceFiles.find((f) => f.id === id);
        if (fileToRemove?.url) {
          URL.revokeObjectURL(fileToRemove.url);
        }
        set({
          request: {
            ...request,
            referenceFiles: request.referenceFiles.filter((f) => f.id !== id),
          },
        });
      },

      updateReferenceFileCaption: (id, caption) => {
        const { request } = get();
        set({
          request: {
            ...request,
            referenceFiles: request.referenceFiles.map((f) =>
              f.id === id ? { ...f, caption } : f
            ),
          },
        });
      },

      // Low tier images
      addLowTierImage: (file) => {
        const { request } = get();
        if (request.lowTierImages.length < ORIGINALS_VALIDATION.lowTier.maxReferenceImages) {
          const newImage: LowTierImage = {
            id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            url: URL.createObjectURL(file),
            name: file.name,
          };
          set({
            request: {
              ...request,
              lowTierImages: [...request.lowTierImages, newImage],
            },
          });
        }
      },

      removeLowTierImage: (id) => {
        const { request } = get();
        const imageToRemove = request.lowTierImages.find((img) => img.id === id);
        if (imageToRemove?.url && imageToRemove.url.startsWith('blob:')) {
          URL.revokeObjectURL(imageToRemove.url);
        }
        set({
          request: {
            ...request,
            lowTierImages: request.lowTierImages.filter((img) => img.id !== id),
          },
        });
      },

      setPersonalImage: (file, url = null) => {
        const { request } = get();
        // Revoke old blob URL if exists
        if (request.personalImageUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(request.personalImageUrl);
        }
        set({
          request: {
            ...request,
            personalImage: file,
            personalImageUrl: file ? (url || URL.createObjectURL(file)) : null,
            personalImageId: null,
          },
        });
      },

      toggleUpgrade: (upgradeId) => {
        const { request } = get();
        const hasUpgrade = request.selectedUpgrades.includes(upgradeId);
        set({
          request: {
            ...request,
            selectedUpgrades: hasUpgrade ? [] : [upgradeId],
          },
        });
      },

      setBriefDescription: (description) => {
        set({
          request: {
            ...get().request,
            briefDescription: description,
          },
        });
      },

      // Step 2 - Quote
      setQuoteData: (data) =>
        set({ quote: { ...get().quote, ...data } }),

      // Step 3 - Retainer
      setRetainerPaid: (paid, transactionId) =>
        set({
          retainer: {
            ...get().retainer,
            retainerPaid: paid,
            paymentDate: paid ? new Date().toISOString() : null,
            transactionId: transactionId || null,
          },
        }),

      // Step 4 - Creation
      setArtworkUrl: (url) =>
        set({
          creation: {
            ...get().creation,
            artworkUrl: url,
            artworkCreatedAt: new Date().toISOString(),
          },
        }),

      addRefinement: (feedback) => {
        const { creation } = get();
        if (creation.refinements.length < creation.maxRefinements) {
          set({
            creation: {
              ...creation,
              refinements: [
                ...creation.refinements,
                {
                  feedback,
                  submittedAt: new Date().toISOString(),
                  respondedAt: null,
                },
              ],
            },
          });
        }
      },

      // Step 5 - Approval
      approveArtwork: () =>
        set({
          approval: {
            ...get().approval,
            approved: true,
            approvedAt: new Date().toISOString(),
            digitalMasterFile: get().creation.artworkUrl,
          },
        }),

      // Step 6 - Physical
      setPhysicalField: (field, value) => {
        // If setting edition, sync legacy fields
        if (field === 'selectedEdition' && typeof value === 'string') {
          const edition = SONIC_PHYSICAL_EDITIONS.find(e => e.id === value);
          if (edition) {
            set({
              physical: {
                ...get().physical,
                selectedEdition: value as SonicPhysicalEditionId,
                // Sync legacy fields for backward compatibility
                selectedSize: edition.size.toLowerCase() as SonicSizeId,
                selectedMaterial: edition.materialId as SonicMaterialId,
                selectedFrame: edition.isFramed,
              },
            });
            return;
          }
        }
        set({ physical: { ...get().physical, [field]: value } });
      },

      setShippingField: (field, value) =>
        set({
          physical: {
            ...get().physical,
            shippingAddress: { ...get().physical.shippingAddress, [field]: value },
          },
        }),

      // Physical delivery price calculation
      // Physical print (Medium Canvas Framed) is included in commission price for ALL tiers
      // Optional upgrades are handled separately via tier upgradeOptions
      getPhysicalPrice: () => {
        // Physical print is included in all commission prices
        // Returns 0 - upgrades are handled via calculateLowTierPrice or admin-set pricing
        return { total: 0, breakdown: [] };
      },

      // Physical delivery validation
      getPhysicalValidationErrors: () => {
        const { physical } = get();
        const errors: Record<string, string> = {};
        const addr = physical.shippingAddress;

        if (!addr.fullName.trim()) errors['shipping.fullName'] = 'Full name is required';
        if (!addr.line1.trim()) errors['shipping.line1'] = 'Address is required';
        if (!addr.city.trim()) errors['shipping.city'] = 'City is required';
        if (!addr.postalCode.trim()) {
          errors['shipping.postalCode'] = 'Postal code is required';
        } else if (!/^[a-zA-Z0-9\s\-]{2,15}$/.test(addr.postalCode.trim())) {
          errors['shipping.postalCode'] = 'Please enter a valid postal code';
        }
        if (!addr.country.trim()) errors['shipping.country'] = 'Country is required';
        if (!addr.phone.trim()) {
          errors['shipping.phone'] = 'Phone number is required';
        } else {
          const digits = addr.phone.replace(/\D/g, '').length;
          if (digits < 7 || !/^\+?[\d\s\-().]{7,20}$/.test(addr.phone.trim())) {
            errors['shipping.phone'] = 'Please enter a valid phone number (at least 7 digits)';
          }
        }

        return errors;
      },

      // Check if physical step can proceed
      canProceedPhysical: () => {
        const errors = get().getPhysicalValidationErrors();
        return Object.keys(errors).length === 0;
      },

      // Order management
      setProjectId: (id) =>
        set({
          order: {
            ...get().order,
            projectId: id,
            createdAt: new Date().toISOString(),
          },
        }),

      setStatus: (status) =>
        set({
          order: {
            ...get().order,
            status,
            completedAt: status === 'COMPLETED' ? new Date().toISOString() : get().order.completedAt,
          },
        }),

      // Backend submission - Upload voice file
      uploadVoice: async () => {
        const { request } = get();
        if (!request.voiceFile) return request.voiceUrl;

        set({ isSubmitting: true, error: null });

        try {
          const response = await requestService.uploadSoundByteVoice(request.voiceFile);
          if (response.success && response.data) {
            set({
              request: { ...get().request, voiceUrl: response.data.url },
              isSubmitting: false,
            });
            return response.data.url;
          } else {
            set({ error: response.error?.message || 'Failed to upload voice', isSubmitting: false });
            return null;
          }
        } catch (err) {
          set({ error: 'Failed to upload voice file', isSubmitting: false });
          return null;
        }
      },

      // Backend submission - Upload reference files
      uploadReferences: async () => {
        const { request } = get();
        const filesToUpload = request.referenceFiles.filter((f) => f.file !== null);

        if (filesToUpload.length === 0) {
          return request.referenceFiles.map((f) => ({
            id: f.id,
            url: f.url,
            name: f.name,
            type: f.type,
          }));
        }

        set({ isSubmitting: true, error: null });

        try {
          const files = filesToUpload.map((f) => f.file!);
          const response = await requestService.uploadSoundByteReferences(files);

          if (response.success && response.data?.files) {
            // Map uploaded files back to reference files
            const uploadedFiles = response.data.files;
            const updatedRefs = request.referenceFiles.map((ref, index) => {
              const uploaded = uploadedFiles[index];
              if (uploaded && ref.file) {
                return {
                  ...ref,
                  id: uploaded.id,
                  url: uploaded.url,
                  file: null,
                };
              }
              return ref;
            });

            set({
              request: { ...get().request, referenceFiles: updatedRefs },
              isSubmitting: false,
            });

            return updatedRefs.map((f) => ({
              id: f.id,
              url: f.url,
              name: f.name,
              type: f.type,
            }));
          } else {
            set({ error: response.error?.message || 'Failed to upload references', isSubmitting: false });
            return [];
          }
        } catch (err) {
          set({ error: 'Failed to upload reference files', isSubmitting: false });
          return [];
        }
      },

      // Backend submission - Upload low tier images (personal + reference images)
      uploadLowTierImages: async () => {
        const { request } = get();
        const uploadedImages: { id: string; url: string; name: string }[] = [];

        // Upload personal image if exists
        if (request.personalImage) {
          try {
            const response = await requestService.uploadSoundByteReferences([request.personalImage]);
            if (response.success && response.data?.files?.[0]) {
              const uploaded = response.data.files[0];
              set({
                request: {
                  ...get().request,
                  personalImageUrl: uploaded.url,
                  personalImageId: uploaded.id,
                  personalImage: null,
                },
              });
            }
          } catch (err) {
            // Failed to upload personal image
          }
        }

        // Upload low tier reference images
        const imagesToUpload = request.lowTierImages.filter((img) => img.file !== null);
        if (imagesToUpload.length > 0) {
          try {
            const files = imagesToUpload.map((img) => img.file!);
            const response = await requestService.uploadSoundByteReferences(files);
            if (response.success && response.data?.files) {
              const uploadedFiles = response.data.files;
              const updatedImages = request.lowTierImages.map((img, index) => {
                const uploaded = uploadedFiles[index];
                if (uploaded && img.file) {
                  uploadedImages.push({ id: uploaded.id, url: uploaded.url, name: img.name });
                  return { ...img, url: uploaded.url, file: null };
                }
                if (!img.file && img.url.startsWith('http')) {
                  uploadedImages.push({ id: img.id, url: img.url, name: img.name });
                }
                return img;
              });
              set({
                request: { ...get().request, lowTierImages: updatedImages },
              });
            }
          } catch (err) {
            // Failed to upload low tier images
          }
        } else {
          // Return existing uploaded images
          request.lowTierImages.forEach((img) => {
            if (img.url.startsWith('http')) {
              uploadedImages.push({ id: img.id, url: img.url, name: img.name });
            }
          });
        }

        return uploadedImages;
      },

      // Backend submission - Submit the request
      submitRequest: async () => {
        const { request, selectedTier, uploadVoice, uploadReferences, getValidationErrors } = get();
        set({ isSubmitting: true, error: null });

        // Validate required fields before submission
        const validationErrors = getValidationErrors();
        if (validationErrors.length > 0) {
          set({
            error: validationErrors.join('. '),
            isSubmitting: false,
          });
          return false;
        }

        try {
          // 1. Upload voice file if exists
          const voiceUrl = await uploadVoice();

          // 2. Build tier-specific data for _tierData
          const tierConfig = selectedTier ? getOriginalsTier(selectedTier) : null;
          let tierData: OriginalsTierData = {
            tier: selectedTier || 'medium',
          };

          // 3. Handle tier-specific uploads and data
          // Get physical config (applies to all tiers)
          const { physical, getPhysicalPrice } = get();
          const physicalPricing = getPhysicalPrice();
          const physicalConfig = {
            sizeId: physical.selectedSize || 'medium',
            materialId: physical.selectedMaterial || 'paper',
            withFraming: physical.selectedFrame,
            shippingAddress: { ...physical.shippingAddress },
            price: physicalPricing.total,
          };

          if (selectedTier === 'low') {
            // Low tier: Upload images and build tier data
            const uploadedImages = await get().uploadLowTierImages();
            const { total: commissionTotal } = calculateLowTierPrice(request.selectedUpgrades);
            const totalWithPhysical = commissionTotal + physicalPricing.total;

            tierData = {
              ...tierData,
              personalImageUrl: get().request.personalImageUrl || undefined,
              lowTierImages: uploadedImages,
              selectedUpgrades: request.selectedUpgrades,
              briefDescription: request.briefDescription || undefined,
              totalPrice: totalWithPhysical,
              physicalConfig,
              // Payment tracking (initialized as unpaid)
              fullPaymentPaid: false,
            };
          } else {
            // Medium/High tier: Upload reference files and build tier data
            const referenceFiles = await uploadReferences();

            tierData = {
              ...tierData,
              retainerAmount: tierConfig?.retainerAmount || 3000,
              retainerPaid: false,
              // Use 999 for unlimited, otherwise use config value or default to 2
              maxRefinements: selectedTier === 'high' ? 999 : (typeof tierConfig?.revisions === 'number' ? tierConfig.revisions : 2),
              refinementCount: 0,
              // Include selected upgrades (material upgrades from canvas to aluminium)
              selectedUpgrades: request.selectedUpgrades,
              physicalConfig,
            };

            // Store reference files in the standard field
            if (referenceFiles.length > 0) {
              set({
                request: {
                  ...get().request,
                  referenceFiles: get().request.referenceFiles.map((f, i) => ({
                    ...f,
                    url: referenceFiles[i]?.url || f.url,
                    file: null,
                  })),
                },
              });
            }
          }

          // 4. Prepare story pillars with _tierData
          const storyPillars: Record<string, unknown> = {
            // Store tier data in _tierData field
            _tierData: tierData,
          };

          // Add story pillar content for Medium/High tier
          if (selectedTier !== 'low') {
            if (request.storyPillars.meaning.notes || request.storyPillars.meaning.tags.length > 0) {
              storyPillars.meaning = { notes: request.storyPillars.meaning.notes, tags: request.storyPillars.meaning.tags };
            }
            if (request.storyPillars.places.notes || request.storyPillars.places.tags.length > 0) {
              storyPillars.places = { notes: request.storyPillars.places.notes, tags: request.storyPillars.places.tags };
            }
            if (request.storyPillars.passions.notes || request.storyPillars.passions.tags.length > 0) {
              storyPillars.passions = { notes: request.storyPillars.passions.notes, tags: request.storyPillars.passions.tags };
            }
            if (request.storyPillars.people.notes || request.storyPillars.people.tags.length > 0) {
              storyPillars.people = { notes: request.storyPillars.people.notes, tags: request.storyPillars.people.tags };
            }
            if (request.storyPillars.personality.notes || request.storyPillars.personality.tags.length > 0) {
              storyPillars.personality = { notes: request.storyPillars.personality.notes, tags: request.storyPillars.personality.tags };
            }
            if (request.storyPillars.favorites.notes || request.storyPillars.favorites.tags.length > 0) {
              storyPillars.favorites = { notes: request.storyPillars.favorites.notes, tags: request.storyPillars.favorites.tags };
            }
          }

          // 5. Build request payload
          const requestPayload: Record<string, unknown> = {
            projectName: request.projectName,
            projectDescription: selectedTier === 'low'
              ? (request.briefDescription || 'Low tier commission')
              : request.projectDescription,
            inspiration: selectedTier === 'low'
              ? 'Artist-led interpretation'
              : request.inspiration,
            storyPillars,
            voiceUrl: voiceUrl || undefined,
            // Include waveform data if available
            waveformGuideBase64: request.waveformGuideBase64 || undefined,
          };

          // Add tier-specific fields
          if (selectedTier === 'low') {
            // For low tier, convert lowTierImages + personalImage to referenceFiles format
            const lowTierReferenceFiles: { id: string; url: string; name: string; type: string; caption: string }[] = [];

            // Add personal image as first reference file. Skip if we don't have
            // the FileUpload row id from the upload step — without it, the admin
            // proxy can't re-sign the URL and the image will appear blank once
            // the original signed URL expires.
            const personalImageUrl = get().request.personalImageUrl;
            const personalImageId = get().request.personalImageId;
            if (personalImageUrl && personalImageUrl.startsWith('http') && personalImageId) {
              lowTierReferenceFiles.push({
                id: personalImageId,
                url: personalImageUrl,
                name: 'Personal Image',
                type: 'image',
                caption: 'Personal/portrait image',
              });
            }

            // Add low tier reference images
            const lowTierImages = tierData.lowTierImages || [];
            lowTierImages.forEach((img) => {
              if (img.url && img.url.startsWith('http')) {
                lowTierReferenceFiles.push({
                  id: img.id,
                  url: img.url,
                  name: img.name,
                  type: 'image',
                  caption: '',
                });
              }
            });

            if (lowTierReferenceFiles.length > 0) {
              Object.assign(requestPayload, {
                referenceFiles: lowTierReferenceFiles,
              });
            }
          } else {
            // Medium/High tier specific fields
            Object.assign(requestPayload, {
              colorPreferences: request.colorPreferences,
              stylePreferences: request.stylePreferences,
              preferredSize: request.preferredSize,
              preferredMaterial: request.preferredMaterial,
              wantsFraming: request.wantsFraming ?? undefined,
              desiredTimeline: request.desiredTimeline,
              specificDate: request.specificDate || undefined,
              isGift: request.isGift,
              giftOccasion: request.giftOccasion || undefined,
              intendedUse: request.intendedUse || undefined,
              additionalNotes: request.additionalNotes || undefined,
              referenceFiles: get().request.referenceFiles
                .filter((f) => f.url.startsWith('http'))
                .map((f) => ({ id: f.id, url: f.url, name: f.name, type: f.type, caption: f.caption })),
            });
          }

          // 6. Submit to backend
          const response = await requestService.createSoundByteRequest(requestPayload as unknown as Parameters<typeof requestService.createSoundByteRequest>[0]);

          if (response.success && response.data) {
            set({
              order: {
                ...get().order,
                projectId: response.data.id,
                status: 'REQUEST_SUBMITTED',
                createdAt: new Date().toISOString(),
              },
              isSubmitting: false,
              error: null,
            });
            return true;
          } else {
            set({
              error: response.error?.message || 'Failed to submit request',
              isSubmitting: false,
            });
            return false;
          }
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to submit request',
            isSubmitting: false,
          });
          return false;
        }
      },

      // Reset
      resetWizard: () =>
        set({
          selectedTier: null,
          currentStep: 0,
          request: { ...initialRequest },
          quote: { ...initialQuote },
          retainer: { ...initialRetainer },
          creation: { ...initialCreation },
          approval: { ...initialApproval },
          physical: { ...initialPhysical },
          order: { ...initialOrder },
          isSubmitting: false,
          error: null,
        }),

      setSubmitting: (submitting) => set({ isSubmitting: submitting }),
      setError: (error) => set({ error }),
    }),
    {
      name: 'soundbyte-originals',
      partialize: (state) => ({
        // Persist tier selection
        selectedTier: state.selectedTier,
        // Persist progress (except File objects and sensitive data)
        currentStep: state.currentStep,
        request: {
          projectName: state.request.projectName,
          projectDescription: state.request.projectDescription,
          inspiration: state.request.inspiration,
          storyPillars: state.request.storyPillars,
          // Reference files - only persist files with real URLs (not blob:)
          referenceFiles: state.request.referenceFiles
            .filter((f) => f.url.startsWith('http'))
            .map((f) => ({
              ...f,
              file: null,
            })),
          // Low tier images - only persist with real URLs
          lowTierImages: state.request.lowTierImages
            .filter((img) => img.url.startsWith('http'))
            .map((img) => ({
              ...img,
              file: null,
            })),
          lowTierImageUrls: state.request.lowTierImageUrls,
          // Personal image - only persist real URL
          personalImage: null,
          personalImageUrl: state.request.personalImageUrl?.startsWith('http')
            ? state.request.personalImageUrl
            : null,
          personalImageId: state.request.personalImageUrl?.startsWith('http')
            ? state.request.personalImageId
            : null,
          selectedUpgrades: state.request.selectedUpgrades,
          briefDescription: state.request.briefDescription,
          // Standard fields
          colorPreferences: state.request.colorPreferences,
          stylePreferences: state.request.stylePreferences,
          intendedUse: state.request.intendedUse,
          additionalNotes: state.request.additionalNotes,
          // Only persist voiceUrl if it's a real URL (not blob:)
          voiceUrl: state.request.voiceUrl?.startsWith('http') ? state.request.voiceUrl : null,
          voiceDuration: state.request.voiceUrl?.startsWith('http') ? state.request.voiceDuration : 0,
          waveformGuideBase64: state.request.waveformGuideBase64,
          voiceFile: null,
          preferredSize: state.request.preferredSize,
          preferredMaterial: state.request.preferredMaterial,
          wantsFraming: state.request.wantsFraming,
          desiredTimeline: state.request.desiredTimeline,
          specificDate: state.request.specificDate,
          isGift: state.request.isGift,
          giftOccasion: state.request.giftOccasion,
        },
        order: state.order,
        // Don't persist payment/sensitive data
      }),
    }
  )
);

// Selector hooks
export const useOriginalsTier = () => useOriginalsStore((state) => state.selectedTier);
export const useOriginalsStep = () => useOriginalsStore((state) => state.currentStep);
export const useOriginalsRequest = () => useOriginalsStore((state) => state.request);
export const useOriginalsQuote = () => useOriginalsStore((state) => state.quote);
export const useOriginalsRetainer = () => useOriginalsStore((state) => state.retainer);
export const useOriginalsCreation = () => useOriginalsStore((state) => state.creation);
export const useOriginalsApproval = () => useOriginalsStore((state) => state.approval);
export const useOriginalsPhysical = () => useOriginalsStore((state) => state.physical);
export const useOriginalsOrder = () => useOriginalsStore((state) => state.order);
export const useOriginalsIsLowTier = () => useOriginalsStore((state) => state.selectedTier === 'low');
export const useOriginalsPhysicalPrice = () => useOriginalsStore((state) => state.getPhysicalPrice());
